import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { MovementsList } from '@/common/components/MovementsList/MovementsList';
import { PageLayout } from '@/common/components/PageLayout/PageLayout';
import { Section } from '@/common/components/Section/Section';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { ROUTES } from '@/common/constants/routes';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useGetBudgetEmployees } from '@/common/integrations/menhir/employee/budgets/budgets.hooks';
import { IEmployeeBudget } from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { useBudgetMovements } from '@/web/hooks/useMovements';
import { ArrayUtils } from '@coverflex-tech/hypernova-utils';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { BudgetsContextMenu } from '../components/BudgetsContextMenu/BudgetsContextMenu';
import {
  BudgetDetailCard,
  BudgetDetailCardSkeleton,
} from './BudgetDetailCard/BudgetDetailCard';
import { BudgetLearningCenter } from './BudgetLearningCenter/BudgetLearningCenter';

export function BudgetDetailPage() {
  const { budgetId } = useLocalSearchParams<{ budgetId: string }>();
  const [showLearningCentre, setShowLearningCentre] = useState(false);

  const { budgetEmployees, isInitialLoading: isLoadingBudget } =
    useGetBudgetEmployees(budgetId);

  const employeeBudget = budgetEmployees?.[0];

  const onOpenLearningCentre = () => {
    AnalyticsService.trackCustomEvent(
      'budgets_detail_learning_center_button_clicked'
    );
    setShowLearningCentre(true);
  };

  const paymentMethod = employeeBudget?.budget.paymentMethod;
  const choices = budgetEmployees?.map((eb) => ({
    id: eb.id,
    name: eb.displayName ?? eb.budget.name,
  }));

  return (
    <PageLayout
      headerProps={{
        title: (
          <BudgetDetailTitle
            name={employeeBudget?.budget.name}
            isLoadingBudget={isLoadingBudget}
          />
        ),
        description: (
          <BudgetDetailDescription
            description={employeeBudget?.budget.description}
            isLoadingBudget={isLoadingBudget}
            onReadMore={onOpenLearningCentre}
          />
        ),
        infoButton: {
          onPress: onOpenLearningCentre,
        },
        primaryAction:
          paymentMethod && paymentMethod !== 'payroll' ? (
            <BudgetsContextMenu budgets={choices} actionType={paymentMethod} />
          ) : undefined,
        backButton: true,
      }}
    >
      {isLoadingBudget ? (
        <BudgetDetailCardSkeleton />
      ) : (
        budgetEmployees?.map((budgetEmloyee, idx) => (
          <BudgetDetailCard key={idx} budgetEmployee={budgetEmloyee} />
        ))
      )}
      {isLoadingBudget ? (
        <BudgetDetailActivitySkeleton />
      ) : (
        <BudgetDetailActivitySection budgetEmployees={budgetEmployees} />
      )}
      {showLearningCentre && employeeBudget && (
        <BudgetLearningCenter
          employeeBudget={employeeBudget}
          onClose={() => setShowLearningCentre(false)}
        />
      )}
    </PageLayout>
  );
}

type BudgetDetailActivitySectionProps = {
  budgetEmployees?: IEmployeeBudget[];
};

function BudgetDetailActivitySection({
  budgetEmployees,
}: BudgetDetailActivitySectionProps) {
  const { t } = useTranslation();
  const router = useRouter();

  const pocketIds =
    budgetEmployees?.map((eb) => eb.pocketId).filter(ArrayUtils.removeFalsey) ??
    [];

  const { movements, isLoading: isLoadingMovements } = useBudgetMovements({
    byPocketId: true,
    pocketIds,
    perPage: 5,
  });

  return (
    <Section
      title={t('general.labels.latest_activity')}
      action={
        <Button
          variant="tertiary"
          size="sm"
          onPress={() =>
            router.navigate({
              pathname: ROUTES.BUDGETS_ACTIVITY,
              params: {
                product: 'budgets',
                budget: pocketIds[0],
              },
            })
          }
        >
          {t('general.see_all')}
        </Button>
      }
    >
      <MovementsList
        movements={movements?.list ?? []}
        loading={isLoadingMovements}
      />
    </Section>
  );
}

function BudgetDetailActivitySkeleton() {
  const { t } = useTranslation();
  return (
    <Section
      title={t('general.labels.latest_activity')}
      action={<SkeletonInline className="w-[10rem]" />}
    >
      <MovementsList movements={[]} loading />
    </Section>
  );
}

export function BudgetDetailTitle({
  name = '',
  isLoadingBudget,
}: {
  name?: string;
  isLoadingBudget: boolean;
}) {
  return isLoadingBudget ? (
    <SkeletonInline className="h-2xl w-[30rem]" />
  ) : (
    name
  );
}

export function BudgetDetailDescription({
  description = '',
  onReadMore,
  isLoadingBudget,
}: {
  description?: string;
  onReadMore?: () => void;
  isLoadingBudget: boolean;
}) {
  const { t } = useTranslation();
  const breakpoints = useBreakpoints(['sm']);
  const maxChars = breakpoints.sm ? 250 : 150;

  return isLoadingBudget ? (
    <SkeletonInline className="h-[2rem] w-[50rem]" />
  ) : description.length > maxChars ? (
    <>
      {`${description.slice(0, maxChars)}... `}
      <TextAction onPress={onReadMore}>{t('general.read_more')}</TextAction>
    </>
  ) : (
    description
  );
}
